import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import Select from "react-select";
import Swal from "sweetalert2";
import Table from "../../core/pagination/datatable";
import { fetchAccountHeads } from "../../core/redux/slices/accountHeadSlice";
import { getExpenseReport } from "../../core/api/reportService";

const today = () => new Date().toISOString().split("T")[0];
const DEFAULT_FILTERS = { from: today(), to: today(), accountHeadId: "" };
const fmt = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;

const ExpenseReport = () => {
  const dispatch = useDispatch();
  const accountHeads = useSelector((s) => s.accountHeads.list);

  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  const headOptions = [{ value: "", label: "All Account Heads" }, ...accountHeads.map((h) => ({ value: h.id, label: h.accountName }))];

  useEffect(() => { dispatch(fetchAccountHeads()); }, [dispatch]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { runReport(); }, []);

  const runReport = async (f = filters) => {
    setLoading(true);
    try {
      const res = await getExpenseReport(f);
      const payload = res.data || {};
      setData(payload.records || payload || []);
      setSummary(payload.summary || null);
    } catch {
      Swal.fire("Error", "Failed to load report.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFilters(DEFAULT_FILTERS);
    runReport(DEFAULT_FILTERS);
  };

  const columns = [
    { title: "Date", dataIndex: "expenseDate", width: 110, render: (v) => v ? new Date(v).toLocaleDateString("en-IN") : "—" },
    { title: "Account Head", dataIndex: "accountName" },
    { title: "Remarks", dataIndex: "remarks", ellipsis: true },
    { title: "Payment Mode", dataIndex: "paymentType", width: 120 },
    { title: "Reference", dataIndex: "expenseEntryNo", width: 150 },
    { title: "Amount", dataIndex: "amount", align: "right", width: 130, render: fmt, sorter: (a, b) => (a.amount || 0) - (b.amount || 0) },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="page-title"><h4>Expense Report</h4><h6>Expense-wise summary by date and account head</h6></div>
        </div>
        <div className="card mb-3">
          <div className="card-body">
            <div className="row g-2 align-items-end">
              <div className="col-auto">
                <label className="form-label small mb-1">From</label>
                <input type="date" className="form-control form-control-sm" value={filters.from}
                  onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))} />
              </div>
              <div className="col-auto">
                <label className="form-label small mb-1">To</label>
                <input type="date" className="form-control form-control-sm" value={filters.to}
                  onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))} />
              </div>
              <div className="col" style={{ minWidth: 200, maxWidth: 280 }}>
                <label className="form-label small mb-1">Account Head</label>
                <Select classNamePrefix="react-select" options={headOptions}
                  value={headOptions.find((o) => o.value && o.value === filters.accountHeadId) || null}
                  placeholder="All Account Heads"
                  isClearable
                  onChange={(opt) => setFilters((f) => ({ ...f, accountHeadId: opt?.value || "" }))} />
              </div>
              <div className="col-auto">
                <button className="btn btn-primary" onClick={() => runReport()} disabled={loading}>
                  {loading ? "Loading..." : "Search"}
                </button>
              </div>
              <div className="col-auto">
                <button className="btn btn-dark" onClick={handleReset} disabled={loading}>
                  Reset
                </button>
              </div>
            </div>
          </div>
        </div>

        {summary && (
          <div className="row mb-3">
            {[
              { label: "Total Expenses", value: fmt(summary.totalAmount), color: "danger" },
              { label: "Entries", value: summary.count, color: "secondary" },
            ].map((s) => (
              <div key={s.label} className="col-6 col-md-3 mb-2">
                <div className={`card border-${s.color} text-center py-2`}>
                  <div className={`text-${s.color} fs-6 fw-bold`}>{s.value}</div>
                  <div className="small text-muted">{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-responsive">
              <Table columns={columns} dataSource={data} loading={loading} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpenseReport;
